"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readQuantity } from '@/lib/tools/numeric-input'

const FALLBACK_RELATED = [
  "cooking-temperature-converter",
  "recipe-measurement-converter",
  "kitchen-timer",
  "refrigerator-size-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

interface Ingredient {
  original: string
  amount: number
  unit: string
  name: string
  scaled: string
}

// Safe fraction parser - handles "1/2", "1 1/2", "2.5" etc. without using eval()
const parseFraction = (str: string): number => {
  const trimmed = str.trim()
  const mixedMatch = trimmed.match(/^(\d+)\s+(\d+)\/(\d+)$/)
  if (mixedMatch) {
    const whole = parseInt(mixedMatch[1], 10)
    const numerator = parseInt(mixedMatch[2], 10)
    const denominator = parseInt(mixedMatch[3], 10)
    if (denominator === 0) return NaN
    return whole + numerator / denominator
  }
  const fractionMatch = trimmed.match(/^(\d+)\/(\d+)$/)
  if (fractionMatch) {
    const numerator = parseInt(fractionMatch[1], 10)
    const denominator = parseInt(fractionMatch[2], 10)
    if (denominator === 0) return NaN
    return numerator / denominator
  }
  const num = parseFloat(trimmed)
  return isNaN(num) ? 0 : num
}

const parseIngredient = (line: string): Ingredient | null => {
  const match = line.match(/^([\d\s./]+)\s*([a-zA-Z]+)?\s+(.+)$/)
  if (match) {
    const amount = parseFraction(match[1])
    const unit = match[2] || ""
    const name = match[3]
    return { original: line, amount, unit, name, scaled: "" }
  }
  return null
}

export function ServingSizeAdjusterPageClient() {
  const meta = TOOL_METADATA["recipe-serving-size-adjuster"]

  const [recipeServings, setRecipeServings] = useState("4")
  const [desiredServings, setDesiredServings] = useState("8")
  const [ingredients, setIngredients] = useState(
    "2 cups flour\n1/2 cup sugar\n3 eggs\n1 teaspoon vanilla"
  )

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    const original = readQuantity(recipeServings, { min: 1, max: 100 })
    const desired = readQuantity(desiredServings, { min: 1, max: 100 })
    if (original === null || desired === null || ingredients === "") return null

    const scaleFactor = desired / original
    const lines = ingredients.split("\n").filter((l) => l.trim())

    const scaledIngredients: Ingredient[] = lines.map((line) => {
      const parsed = parseIngredient(line.trim())
      if (parsed) {
        const scaledAmount = parsed.amount * scaleFactor
        let formattedAmount = scaledAmount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/\.?0+$/, "")
        if (Math.abs(scaledAmount - 0.25) < 0.01) formattedAmount = "1/4"
        else if (Math.abs(scaledAmount - 0.33) < 0.01) formattedAmount = "1/3"
        else if (Math.abs(scaledAmount - 0.5) < 0.01) formattedAmount = "1/2"
        else if (Math.abs(scaledAmount - 0.67) < 0.01) formattedAmount = "2/3"
        else if (Math.abs(scaledAmount - 0.75) < 0.01) formattedAmount = "3/4"
        parsed.scaled = `${formattedAmount} ${parsed.unit} ${parsed.name}`.replace(/\s+/g, " ").trim()
        return parsed
      }
      return { original: line, amount: 0, unit: "", name: line, scaled: line }
    })

    return { scaleFactor, scaledIngredients }
  }, [recipeServings, desiredServings, ingredients])

  return (
    <ToolPageShell
      toolName={meta.name}
      heroEyebrow={meta.category}
      heroTitlePre="Scale any recipe to"
      heroTitleItalic="any"
      heroTitlePost="number of servings."
      heroSubtitle={meta.mediumDescription}
      heroMeta={["⏱ ~20 sec", "Culinary scaling principles"]}
      heroShare={<ToolShareBar title={meta.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "1×", label: "Linear scaling", note: "Most ingredients" },
            { value: "0.75×", label: "Spice scaling", note: "Conservative" },
            { value: "+25%", label: "Time for 2× batch", note: "Adjustment" },
            { value: "Area", label: "Pan size scales", note: "Not linear" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={meta.faqs.slice(0, 6).map((f) => ({ question: f.question, answer: f.answer }))}
      relatedTools={buildRelated(meta.relatedTools, "recipe-serving-size-adjuster")}
      cta={{
        title: "Bigger recipes need bigger fridge real estate.",
        body: "Browse French door, top-freezer, side-by-side, and counter-depth fridges — every type, sortable by cubic-foot capacity.",
        buttonLabel: "Browse all refrigerators",
        buttonHref: "/best-refrigerators",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Original servings.
                <br />
                Target servings.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Paste ingredients one per line. We handle fractions, decimals, and mixed numbers.
              </p>

              <div className="mt-7 space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                      Original servings
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={100}
                      step={1}
                      value={recipeServings}
                      onChange={(e) => setRecipeServings(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                      Desired servings
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={100}
                      step={1}
                      value={desiredServings}
                      onChange={(e) => setDesiredServings(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Ingredients (one per line)
                  </label>
                  <textarea
                    value={ingredients}
                    onChange={(e) => setIngredients(e.target.value)}
                    rows={8}
                    className="tool-input-clean w-full px-4 py-3 text-[15px] font-mono rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                  />
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Format: amount unit ingredient (e.g., &quot;2 cups flour&quot;).
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }}
                />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-6">
                    Scale factor
                  </div>
                  <div
                    className="font-black"
                    style={{ fontSize: "clamp(36px, 8vw, 88px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}
                  >
                    {result?.scaleFactor.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? "0.00"}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">×</span>
                  </div>
                  <div className="mt-3 text-[14px] text-white/80">
                    {recipeServings} → {desiredServings} servings
                  </div>
                  {result && result.scaledIngredients.length > 0 && (
                    <div className="mt-6 space-y-2 max-h-[260px] overflow-y-auto pr-1">
                      {result.scaledIngredients.map((ing, idx) => (
                        <div key={idx} className="rounded-lg p-3 bg-white/10 border border-white/15">
                          <div className="text-[11px] uppercase tracking-[0.12em] text-white/55 font-bold mb-1">
                            {ing.original}
                          </div>
                          <div className="text-[15px] font-bold tracking-tight">{ing.scaled}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Cold storage for big batches"
          title="French door fridges for batch cooking"
          description="Wide, shallow shelves and door-in-door access — built for the volume of doubled recipes and meal prep."
          collectionType="french-door"
          fallbackCollectionType="bottom-freezer"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                Linear math, <em className="italic text-[#ff9951]">honest</em> warnings.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Most ingredients scale linearly with the desired-to-original serving ratio. Spices, leavening, and cook time follow culinary scaling rules used in professional kitchens.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Scale factor",
                  d: "Desired servings ÷ original servings. We multiply each parsed ingredient amount by this factor.",
                },
                {
                  n: "02",
                  t: "Fraction recognition",
                  d: "0.25 → 1/4, 0.33 → 1/3, 0.5 → 1/2, 0.67 → 2/3, 0.75 → 3/4. Mixed numbers like \"1 1/2\" parse correctly.",
                },
                {
                  n: "03",
                  t: "Real-cooking caveats",
                  d: "Scale spices conservatively (~75%), add ~25% time for doubled batches, and scale pan size by area, not linear inches.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Counter-depth fridges for tighter kitchens"
          description="Same family-size capacity, flush profile — perfect for kitchens where every inch counts."
          collectionType="counter-depth"
          fallbackCollectionType="french-door"
        />
      </section>
    </ToolPageShell>
  )
}
